import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React from "react";

import ContentContainer from "~/components/layout/ContentContainer";

import UserContent from "~/components/UserContent";

import { sanitizeAndParseHTML } from "~/utilities/helpers";
import { formatAttributeValue } from "~/utilities/format";

export const TableWrap = styled.div`
    display: block;
    max-width: 100%;
    position: relative;
    overflow: auto;
    scroll-behavior: smooth;
    margin-bottom: ${({ theme }) => theme.layout.paragraphSpacing};
    max-height: ${({ paginationHeight, theme }) =>
        paginationHeight
            ? `calc(100vh - ${paginationHeight}px - ${theme.layout.gutter})`
            : `90vh`};

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        max-height: ${({ paginationHeight, stickyItemsHeight, theme }) =>
            paginationHeight || stickyItemsHeight
                ? `calc(100vh - ${paginationHeight + stickyItemsHeight}px - ${
                      paginationHeight ? theme.layout.gutter : `0px`
                  })`
                : `90vh`};
    }

    @media (min-width: ${({ theme }) =>
            theme.breakpoints.tabletLandscape}) and (min-height: 600px) {
        max-height: ${({
            filterHeight,
            paginationHeight,
            stickyItemsHeight,
            theme,
        }) =>
            filterHeight || paginationHeight || stickyItemsHeight
                ? `calc(100vh - ${
                      filterHeight + paginationHeight + stickyItemsHeight
                  }px - ${paginationHeight ? theme.layout.gutter : `0px`})`
                : `90vh`};
    }

    &::-webkit-scrollbar {
        width: ${({ theme }) => theme.table.scrollBarWidth};
        height: ${({ theme }) => theme.table.scrollBarWidth};
    }

    &::-webkit-scrollbar-thumb {
        background-color: ${({ theme }) => theme.palette.graySix};
        border-radius: ${({ theme }) => theme.table.scrollBarWidth};
    }
`;

export const StyledTable = styled.table`
    border-collapse: separate;
    min-width: ${({ fullWidthCells }) => (fullWidthCells ? `auto` : `100%`)};
    width: auto;
    table-layout: ${({ fixed }) => (fixed ? `fixed` : `auto`)};
`;

export const Th = styled.th`
    background-color: ${({ theme }) => theme.palette.black};
    color: ${({ theme }) => theme.palette.white};
    font-size: ${({ smallTableHeaders }) =>
        smallTableHeaders ? `1.4rem` : `1.8rem`};
    line-height: 1;
    text-align: left;
    font-weight: 700;
    padding: 20px 15px;
    border-right: ${({ theme }) => `1px solid ${theme.palette.white}`};
    vertical-align: bottom;
    position: sticky;
    top: 0;
    z-index: 1;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        font-size: ${({ smallTableHeaders }) =>
            smallTableHeaders ? `1.4rem` : `2rem`};
    }
`;

export const Tr = styled.tr`
    td {
        background-color: ${({ theme }) => theme.palette.white};
    }

    &:nth-last-of-type(odd) {
        td {
            background-color: ${({ theme }) => theme.palette.borderGray};

            a:not(.button):hover {
                color: ${({ theme }) => theme.palette.darkTeal};
            }
        }
    }
`;

export const Td = styled.td`
    font-size: 1.4rem;
    line-height: 1.125;
    font-weight: 400;
    padding: 13px 15px;
    vertical-align: middle;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        font-size: 1.6rem;
    }
`;

const CellContent = styled(UserContent)`
    a {
        text-decoration: none;
    }
`;

const TableBlock = ({ className, productTable, rows, wrapComponent: Wrap }) =>
    rows.length ? (
        <Wrap {...{ className }}>
            {rows.map(({ cells }, rowIndex) => (
                <Tr key={rowIndex}>
                    {cells.map(({ content, tag }, cellIndex) => {
                        const Cell = tag === "th" ? Th : Td;

                        return (
                            <Cell
                                key={cellIndex}
                                smallTableHeaders={productTable}
                            >
                                {content ? (
                                    productTable ? (
                                        formatAttributeValue(content)
                                    ) : (
                                        <CellContent>
                                            {sanitizeAndParseHTML(content)}
                                        </CellContent>
                                    )
                                ) : null}
                            </Cell>
                        );
                    })}
                </Tr>
            ))}
        </Wrap>
    ) : null;

const StyledTableBlock = styled(TableBlock)`
    border-top: ${({ theme, topBorder }) =>
        topBorder ? `1px solid ${theme.palette.black}` : null};
    td {
        background-color: ${({ theme }) =>
            theme.colors.backgroundColor.section};
    }
`;

const Caption = styled.div`
    margin-top: 10px;
`;

const Table = ({
    bodyRows,
    footRows,
    headRows,
    productTable,
    caption = "",
    fixed = false,
    fullWidthCells = false,
}) => (
    <ContentContainer>
        <TableWrap>
            <StyledTable {...{ fixed, fullWidthCells }}>
                <TableBlock
                    rows={headRows}
                    wrapComponent={"thead"}
                    {...{ productTable }}
                />

                <TableBlock
                    rows={bodyRows}
                    wrapComponent={"tbody"}
                    {...{ productTable }}
                />

                <StyledTableBlock
                    rows={footRows}
                    wrapComponent={"tfoot"}
                    topBorder
                />
            </StyledTable>
            {caption ? (
                <Caption>{sanitizeAndParseHTML(caption)}</Caption>
            ) : null}
        </TableWrap>
    </ContentContainer>
);

Table.propTypes = {
    bodyRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    caption: PropTypes.string,
    fixed: PropTypes.bool,
    footRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    fullWidthCells: PropTypes.bool,
    headRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default Table;
